import React, { useRef, useState } from 'react';
import { Camera, Loader2, CheckCircle2, ImageUp, X, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { driverApi } from '../services/api';
import { toast } from 'sonner';
import CameraCaptureModal from './CameraCaptureModal';
import { downscaleImage } from '../lib/downscaleImage';

/**
 * PodPhotoUploader — 2-slot proof-of-delivery uploader with preview + Save.
 *
 * Flow per slot:
 *   1) Driver taps *Take photo* or *Upload from gallery*.
 *   2) A local preview appears immediately (no upload yet). Driver can Retake
 *      or Save.
 *   3) Tapping *Save* uploads the picture to /api/files then patches the
 *      order via /api/driver/orders/{id}/pod. Only saved photos count.
 *
 * POD photos are OPTIONAL — the DELIVERED button never depends on how many
 * have been saved; it's only a helpful audit trail.
 */
const PodPhotoUploader = ({ orderId, value = [], onChange, disabled }) => {
  const { t } = useTranslation();
  const slots = [0, 1];
  const inputRefs = [useRef(null), useRef(null)];
  const [busy, setBusy] = useState(null);
  const [cameraSlot, setCameraSlot] = useState(null);   // slot idx while camera modal open
  // Local preview state per slot: {file, dataUrl} — not yet uploaded.
  const [pending, setPending] = useState({});

  const openCamera = (i) => setCameraSlot(i);
  const openFilePicker = (i) => () => inputRefs[i].current?.click();

  const setPreview = (i, file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setPending((prev) => ({ ...prev, [i]: { file, dataUrl: reader.result } }));
    };
    reader.readAsDataURL(file);
  };

  const onFileInput = (i) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type?.startsWith('image/')) { toast.error('Please pick an image'); return; }
    setPreview(i, f);
    if (inputRefs[i].current) inputRefs[i].current.value = '';
  };

  const onCameraCapture = (file) => {
    if (cameraSlot == null) return;
    setPreview(cameraSlot, file);
  };

  const cancelPreview = (i) => {
    setPending((prev) => { const n = { ...prev }; delete n[i]; return n; });
  };

  const savePreview = async (i) => {
    const p = pending[i]; if (!p) return;
    setBusy(i);
    try {
      let toUpload = p.file;
      try {
        toUpload = await downscaleImage(p.file, { maxBytes: 5 * 1024 * 1024 });
      } catch (_e) { /* best-effort */ }
      const fd = new FormData();
      fd.append('file', toUpload);
      const { data: uploaded } = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileIds = [...(value.length ? value.map((v) => extractId(v)) : [])];
      fileIds[i] = uploaded.file_id;
      const fresh = await driverApi.uploadPod(orderId, fileIds.filter(Boolean));
      onChange?.(fresh.pod_photos.map((id) => apiUrlFromFileId(id)));
      cancelPreview(i);
      const before = (p.file.size / (1024 * 1024)).toFixed(2);
      const after = (toUpload.size / (1024 * 1024)).toFixed(2);
      toast.success(`Photo ${i + 1} saved ${before !== after ? `(auto-resized ${before}MB → ${after}MB)` : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(null); }
  };

  const count = value.filter(Boolean).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-slate-900">{t('driver.proof_of_delivery', 'Proof of delivery')} <span className="text-[11px] font-normal text-slate-400">— optional</span></h4>
          <p className="text-xs text-slate-500 mt-0.5">{t('driver.pod_help', 'Snap up to 2 photos (camera or gallery) and tap Save. You can always mark the order DELIVERED without them.')}</p>
        </div>
        <div className={`text-xs font-semibold px-2 py-0.5 rounded-full ${count >= 1 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid="pod-counter">
          {count}/2 {count >= 2 && <CheckCircle2 className="h-3 w-3 inline ml-0.5 -mt-0.5" />}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {slots.map((i) => {
          const url = value[i];
          const preview = pending[i];
          return (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50">
              <input
                ref={inputRefs[i]}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onFileInput(i)}
                data-testid={`pod-input-${i}`}
              />
              {preview ? (
                // ------------ PREVIEW STAGE — not yet uploaded ---------------
                <>
                  <img src={preview.dataUrl} alt={`POD ${i + 1} preview`} className="absolute inset-0 w-full h-full object-cover" data-testid={`pod-preview-${i}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute top-1.5 left-1.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded">Preview</div>
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between gap-1">
                    <button
                      type="button"
                      onClick={() => cancelPreview(i)}
                      disabled={busy === i}
                      className="h-7 px-2 rounded-full bg-white/95 text-slate-700 text-[10px] font-semibold hover:bg-white shadow flex items-center gap-1"
                      data-testid={`pod-cancel-${i}`}
                    >
                      <X className="h-3 w-3" /> Retake
                    </button>
                    <button
                      type="button"
                      onClick={() => savePreview(i)}
                      disabled={busy === i}
                      className="h-7 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider shadow flex items-center gap-1 disabled:opacity-60"
                      data-testid={`pod-save-${i}`}
                    >
                      {busy === i
                        ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>
                        : <><Save className="h-3 w-3" /> Save</>}
                    </button>
                  </div>
                </>
              ) : url ? (
                // ------------ SAVED STAGE — server-side ---------------------
                <>
                  <img src={url} alt={`POD ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-1.5 left-1.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Saved
                  </div>
                  <div className="absolute bottom-1.5 right-1.5 flex gap-1">
                    <button
                      onClick={() => openCamera(i)}
                      disabled={disabled || busy != null}
                      className="h-7 w-7 rounded-full bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-700 shadow"
                      data-testid={`pod-retake-camera-${i}`}
                      aria-label="Retake with camera"
                      title="Retake with camera"
                    >
                      <Camera className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={openFilePicker(i)}
                      disabled={disabled || busy != null}
                      className="h-7 w-7 rounded-full bg-white/90 text-slate-700 flex items-center justify-center hover:bg-white shadow"
                      data-testid={`pod-retake-file-${i}`}
                      aria-label="Choose from files"
                      title="Choose from gallery"
                    >
                      <ImageUp className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              ) : (
                // ------------ EMPTY STAGE — call-to-action -----------------
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-1.5">
                  <button
                    onClick={() => openCamera(i)}
                    disabled={disabled}
                    className="w-full flex-1 flex flex-col items-center justify-center gap-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98] transition"
                    data-testid={`pod-take-photo-${i}`}
                    aria-label={`Take photo ${i + 1}`}
                  >
                    <Camera className="h-5 w-5" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider">Take photo {i + 1}</span>
                  </button>
                  <button
                    onClick={openFilePicker(i)}
                    disabled={disabled}
                    className="w-full text-[10px] uppercase tracking-wider text-slate-500 hover:text-emerald-700 flex items-center justify-center gap-1 py-0.5"
                    data-testid={`pod-choose-file-${i}`}
                  >
                    <ImageUp className="h-3 w-3" /> or upload from gallery
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CameraCaptureModal
        open={cameraSlot !== null}
        onClose={() => setCameraSlot(null)}
        onCapture={onCameraCapture}
        title={cameraSlot != null ? `Delivery photo ${cameraSlot + 1} of 2` : 'Take photo'}
      />
    </div>
  );
};

const apiUrlFromFileId = (id) => {
  if (!id) return '';
  if (typeof id === 'string' && id.startsWith('http')) return id;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}/api/files/${id}`;
};

const extractId = (urlOrId) => {
  if (!urlOrId) return null;
  if (typeof urlOrId !== 'string') return null;
  if (urlOrId.startsWith('http')) {
    const m = urlOrId.match(/\/api\/files\/(.+)$/);
    return m ? m[1] : null;
  }
  return urlOrId;
};

export default PodPhotoUploader;
